import React, { useEffect, useState, useRef } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';

const BlinkitCaseStudy = ({ onClose }) => {
  const [content, setContent] = useState('');
  const [headings, setHeadings] = useState([]);
  const [activeSection, setActiveSection] = useState('');
  const scrollContainerRef = useRef(null);

  useEffect(() => {
    // Fetch the markdown file
    fetch('/Blinkit - Engagement & Retention Report.md')
      .then(response => response.text())
      .then(data => {
        setContent(data);
        // Extract headings for table of contents
        const lines = data.split('\n');
        const extractedHeadings = [];
        lines.forEach((line) => {
          let text = '';
          if (line.startsWith('# ')) {
            text = line.substring(2).trim();
            text = text.replace(/\*\*/g, '').replace(/\*/g, '').replace(/`/g, '');
            if (text) {
              extractedHeadings.push({ level: 1, text });
            }
          } else if (line.startsWith('## ')) {
            text = line.substring(3).trim();
            text = text.replace(/\*\*/g, '').replace(/\*/g, '').replace(/`/g, '');
            if (text) {
              extractedHeadings.push({ level: 2, text });
            }
          } else if (line.startsWith('### ')) {
            text = line.substring(4).trim();
            text = text.replace(/\*\*/g, '').replace(/\*/g, '').replace(/`/g, '');
            if (text) {
              extractedHeadings.push({ level: 3, text });
            }
          }
        });
        setHeadings(extractedHeadings);
      })
      .catch(error => console.error('Error loading case study:', error));
  }, []);

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        onClose();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [onClose]);

  const extractTextFromChildren = (children) => {
    if (typeof children === 'string') return children;
    if (Array.isArray(children)) {
      return children.map(child => extractTextFromChildren(child)).join('');
    }
    if (children?.props?.children) {
      return extractTextFromChildren(children.props.children);
    }
    return '';
  };

  const createSlug = (text) => {
    // Remove markdown formatting first
    const cleanText = String(text).replace(/\*\*/g, '').replace(/\*/g, '').replace(/`/g, '');
    return cleanText
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/(^-|-$)/g, '');
  };

  const scrollToSection = (heading) => {
    const slug = createSlug(heading);
    const element = document.getElementById(slug);
    if (element && scrollContainerRef.current) {
      const container = scrollContainerRef.current;
      const elementTop = element.offsetTop;
      const headerOffset = 120;
      container.scrollTo({ 
        top: elementTop - headerOffset, 
        behavior: 'smooth' 
      });
      setActiveSection(slug);
    }
  };

  // Track active section on scroll
  useEffect(() => {
    if (headings.length === 0 || !scrollContainerRef.current) return;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setActiveSection(entry.target.id);
          }
        });
      },
      { 
        root: scrollContainerRef.current,
        rootMargin: '-120px 0px -70% 0px',
        threshold: 0.1
      }
    );

    const timer = setTimeout(() => {
      headings.forEach((heading) => {
        const slug = createSlug(heading.text);
        const element = document.getElementById(slug);
        if (element) {
          observer.observe(element);
        }
      });
    }, 200);

    return () => {
      clearTimeout(timer);
      observer.disconnect();
    };
  }, [headings]);

  return (
    <div 
      ref={scrollContainerRef}
      className="h-screen w-screen bg-[#111111] text-white overflow-y-auto overflow-x-hidden custom-scrollbar"
      style={{ fontFamily: "'FFF Acid Grotesk', sans-serif", scrollBehavior: 'smooth' }}
    >
      <style>{`
        .custom-scrollbar::-webkit-scrollbar {
          width: 8px;
        }
        .custom-scrollbar::-webkit-scrollbar-track {
          background: transparent;
        }
        .custom-scrollbar::-webkit-scrollbar-thumb {
          background: rgba(255, 255, 255, 0.1);
          border-radius: 10px;
        }
        .custom-scrollbar::-webkit-scrollbar-thumb:hover {
          background: rgba(255, 255, 255, 0.2);
        }
        
        /* Firefox */
        .custom-scrollbar {
          scrollbar-width: thin;
          scrollbar-color: rgba(255, 255, 255, 0.1) transparent;
        }
      `}</style>
      {/* Header with close button */}
      <div className="sticky top-0 z-50 bg-[#111111] border-b border-gray-800 px-8 py-6">
        <div className="max-w-6xl mx-auto flex justify-between items-center">
          <h1 className="text-3xl font-bold">Blinkit Case Study</h1>
          <button
            onClick={onClose}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                onClose();
              }
            }}
            className="px-6 py-2 bg-[#3a3a3a] hover:bg-[#4a4a4a] transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-white text-sm tracking-wider"
            tabIndex={0}
            aria-label="Close case study"
          >
            ← BACK
          </button>
        </div>
      </div>

      {/* Content with Sidebar */}
      <div className="flex max-w-[1600px] mx-auto">
        {/* Table of Contents Sidebar */}
        {headings.length > 0 && (
          <aside className="hidden lg:block w-64 flex-shrink-0 sticky top-24 h-[calc(100vh-6rem)] overflow-y-auto custom-scrollbar pr-6">
            <div className="py-6 pl-8">
              <h2 className="text-sm font-semibold text-gray-400 uppercase tracking-wider mb-4">Contents</h2>
              <nav className="space-y-1">
                {headings.filter((_, index) => index !== 0).map((heading, filteredIndex) => {
                  const slug = createSlug(heading.text);
                  const isActive = activeSection === slug;
                  
                  return (
                    <button
                      key={filteredIndex}
                      onClick={() => scrollToSection(heading.text)}
                      className={`
                        block w-full text-left py-2 px-3 transition-all outline-none focus:outline-none border-0
                        ${heading.level === 1 ? 'text-base font-semibold' : ''}
                        ${heading.level === 2 ? 'pl-4 text-sm' : ''}
                        ${heading.level === 3 ? 'pl-8 text-xs' : ''}
                        ${
                          isActive
                            ? 'text-blue-400 font-medium bg-transparent'
                            : 'text-gray-400 hover:text-white bg-transparent'
                        }
                      `}
                      style={{ boxShadow: 'none', border: 'none' }}
                    >
                      {heading.text}
                    </button>
                  );
                })}
              </nav>
            </div>
          </aside>
        )}

        {/* Main Content */}
        <div className="flex-1 px-8 py-12">
        {content ? (
          <div className="markdown-content">
            <ReactMarkdown
              remarkPlugins={[remarkGfm]}
              components={{
                h1: ({ children }) => {
                  const text = extractTextFromChildren(children);
                  const slug = createSlug(text);
                  return (
                    <h1 id={slug} className="text-6xl font-bold mb-8 mt-16 text-white border-b border-gray-700 pb-4 scroll-mt-[120px]">
                      {children}
                    </h1>
                  );
                },
                h2: ({ children }) => {
                  const text = extractTextFromChildren(children);
                  const slug = createSlug(text);
                  return (
                    <h2 id={slug} className="text-4xl font-bold mb-6 mt-12 text-white scroll-mt-[120px]">
                      {children}
                    </h2>
                  );
                },
                h3: ({ children }) => {
                  const text = extractTextFromChildren(children);
                  const slug = createSlug(text);
                  return (
                    <h3 id={slug} className="text-3xl font-semibold mb-5 mt-10 text-gray-200 scroll-mt-[120px]">
                      {children}
                    </h3>
                  );
                },
                p: ({ children }) => (
                  <p className="text-base leading-relaxed mb-5 text-gray-300">
                    {children}
                  </p>
                ),
                ul: ({ children }) => (
                  <ul className="mb-6 space-y-3 text-gray-300">
                    {children}
                  </ul>
                ),
                ol: ({ children }) => (
                  <ol className="mb-6 space-y-3 text-gray-300 list-decimal pl-6">
                    {children}
                  </ol>
                ),
                li: ({ children }) => (
                  <li className="flex items-start gap-3 text-gray-300 leading-relaxed">
                    <span className="text-blue-400 mt-1.5 flex-shrink-0">•</span>
                    <span className="flex-1">{children}</span>
                  </li>
                ),
                strong: ({ children }) => (
                  <strong className="font-bold text-white">
                    {children}
                  </strong>
                ),
                a: ({ href, children }) => (
                  <a
                    href={href}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-blue-400 hover:text-blue-300 underline transition-colors"
                  >
                    {children}
                  </a>
                ),
                table: ({ children }) => (
                  <div className="overflow-x-auto my-8 rounded-lg border border-gray-700">
                    <table className="min-w-full divide-y divide-gray-700">
                      {children}
                    </table>
                  </div>
                ),
                thead: ({ children }) => (
                  <thead className="bg-gray-800">
                    {children}
                  </thead>
                ),
                tbody: ({ children }) => (
                  <tbody className="bg-gray-900 divide-y divide-gray-700">
                    {children}
                  </tbody>
                ),
                tr: ({ children }) => (
                  <tr className="hover:bg-gray-800 transition-colors">
                    {children}
                  </tr>
                ),
                th: ({ children }) => (
                  <th className="px-6 py-4 text-left text-sm font-semibold text-white uppercase tracking-wider">
                    {children}
                  </th>
                ),
                td: ({ children }) => (
                  <td className="px-6 py-4 text-sm text-gray-300 whitespace-normal">
                    {children}
                  </td>
                ),
                blockquote: ({ children }) => (
                  <blockquote className="border-l-4 border-blue-500 pl-4 py-2 my-6 italic text-gray-400 bg-gray-800/50 rounded-r">
                    {children}
                  </blockquote>
                ),
                code: ({ inline, children }) =>
                  inline ? (
                    <code className="bg-gray-800 text-blue-300 px-2 py-1 rounded text-sm font-mono">
                      {children}
                    </code>
                  ) : (
                    <code className="block bg-gray-800 text-blue-300 p-4 rounded-lg overflow-x-auto text-sm font-mono my-4">
                      {children}
                    </code>
                  ),
              }}
            >
              {content}
            </ReactMarkdown>
          </div>
        ) : (
          <div className="flex items-center justify-center h-64">
            <div className="text-center">
              <div className="inline-block animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500 mb-4"></div>
              <p className="text-xl text-gray-400">Loading case study...</p>
            </div>
          </div>
        )}
        </div>
      </div>

      {/* Footer */}
      <div className="border-t border-gray-800 px-8 py-6 mt-12">
        <div className="max-w-6xl mx-auto text-center text-sm text-gray-500">
          <p>Blinkit Engagement & Retention Analysis</p>
          <p className="mt-2">Press ESC to go back</p>
        </div>
      </div>
    </div>
  );
};

export default BlinkitCaseStudy;
